import type { ApiResponseType } from '~/types/api'
import type {
  NodeTypeDTOType,
  ValidationReportType,
  WorkflowCredentialType,
  WorkflowDetailType,
  WorkflowExecuteResultType,
  WorkflowExecutionDetailType,
  WorkflowExecutionListType,
  WorkflowGraphType,
  WorkflowListType,
  WorkflowStatsType,
  WorkflowType
} from '~/types/workflow'
import api, { apiData } from './api'

export const workflowService = {
  list: async (params?: Record<string, unknown>): Promise<WorkflowListType> => {
    return apiData(api.get<ApiResponseType<WorkflowListType>>('/workflows', { params }))
  },
  get: async (id: number | string): Promise<WorkflowDetailType> => {
    return apiData(api.get<ApiResponseType<WorkflowDetailType>>(`/workflows/${id}`))
  },
  create: async (payload: { name: string; description?: string; graph?: WorkflowGraphType }): Promise<WorkflowType> => {
    return apiData(api.post<ApiResponseType<WorkflowType>>('/workflows', payload))
  },
  update: async (id: number, payload: Record<string, unknown>): Promise<WorkflowType> => {
    return apiData(api.put<ApiResponseType<WorkflowType>>(`/workflows/${id}`, payload))
  },
  delete: async (id: number) => {
    return apiData(api.delete<ApiResponseType<boolean>>(`/workflows/${id}`))
  },
  nodeTypes: async (): Promise<NodeTypeDTOType[]> => {
    return apiData(api.get<ApiResponseType<NodeTypeDTOType[]>>('/workflows/node-types'))
  },
  getDraft: async (id: number) => {
    return apiData(
      api.get<
        ApiResponseType<{
          versionId: number
          version: number
          graph: WorkflowGraphType
          revision: number
        }>
      >(`/workflows/${id}/draft`)
    )
  },
  saveDraft: async (id: number, payload: { graph: WorkflowGraphType; revision: number }): Promise<WorkflowType> => {
    return apiData(api.put<ApiResponseType<WorkflowType>>(`/workflows/${id}/draft`, payload))
  },
  validate: async (id: number) => {
    return apiData(api.post<ApiResponseType<ValidationReportType>>(`/workflows/${id}/validate`))
  },
  publish: async (id: number, notes?: string) => {
    return apiData(api.post<ApiResponseType<WorkflowType>>(`/workflows/${id}/publish`, { notes }))
  },
  activate: async (id: number) => {
    return apiData(api.post<ApiResponseType<WorkflowType>>(`/workflows/${id}/activate`))
  },
  deactivate: async (id: number) => {
    return apiData(api.post<ApiResponseType<WorkflowType>>(`/workflows/${id}/deactivate`))
  },
  rollback: async (id: number, version: number) => {
    return apiData(api.post<ApiResponseType<WorkflowType>>(`/workflows/${id}/rollback/${version}`))
  },
  versions: async (id: number, params?: Record<string, unknown>) => {
    return apiData(api.get<ApiResponseType<WorkflowType[]>>(`/workflows/${id}/versions`, { params }))
  },
  execute: async (id: number, payload?: { input?: unknown; idempotencyKey?: string; mode?: string }): Promise<WorkflowExecuteResultType> => {
    return apiData(api.post<ApiResponseType<WorkflowExecuteResultType>>(`/workflows/${id}/execute`, payload ?? {}))
  },
  executions: async (id: number, params?: Record<string, unknown>): Promise<WorkflowExecutionListType> => {
    return apiData(api.get<ApiResponseType<WorkflowExecutionListType>>(`/workflows/${id}/executions`, { params }))
  },
  execution: async (publicId: string): Promise<WorkflowExecutionDetailType> => {
    return apiData(api.get<ApiResponseType<WorkflowExecutionDetailType>>(`/workflows/executions/${publicId}`))
  },
  cancelExecution: async (publicId: string) => {
    return apiData(api.post<ApiResponseType<WorkflowExecutionDetailType>>(`/workflows/executions/${publicId}/cancel`))
  },
  retryExecution: async (publicId: string) => {
    return apiData(api.post<ApiResponseType<WorkflowExecutionDetailType>>(`/workflows/executions/${publicId}/retry`))
  },
  stats: async (id: number, days = 7): Promise<WorkflowStatsType> => {
    return apiData(api.get<ApiResponseType<WorkflowStatsType>>(`/workflows/${id}/stats`, { params: { days } }))
  }
}

export const workflowCredentialService = {
  list: async (): Promise<WorkflowCredentialType[]> => {
    return apiData(api.get<ApiResponseType<WorkflowCredentialType[]>>('/workflow-credentials'))
  },
  create: async (payload: { name: string; type: string; data: Record<string, unknown> }): Promise<WorkflowCredentialType> => {
    return apiData(api.post<ApiResponseType<WorkflowCredentialType>>('/workflow-credentials', payload))
  },
  update: async (id: number, payload: Record<string, unknown>): Promise<WorkflowCredentialType> => {
    return apiData(api.put<ApiResponseType<WorkflowCredentialType>>(`/workflow-credentials/${id}`, payload))
  },
  delete: async (id: number) => {
    return apiData(api.delete<ApiResponseType<boolean>>(`/workflow-credentials/${id}`))
  }
}
